import { useEffect, useState } from 'react'
import { getTicketFields, saveFieldMapping, loadFieldMapping } from '@/services'
import type { TicketField as ZendeskTicketField } from '@/services'
import type { FieldMapping, FieldMappingLabels } from '@/services'
import { VTEX_PROPERTIES } from '@/constants'
import { zafClient } from '@/services/zendesk/zafClient'

export interface LinkedField {
  zendeskField: ZendeskTicketField
  vtexKey: string
}

export function useEditarCampos() {
  const [allFields, setAllFields] = useState<ZendeskTicketField[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [linkedFields, setLinkedFields] = useState<LinkedField[]>([])
  const [showInfo, setShowInfo] = useState(true)

  useEffect(() => {
    getTicketFields()
      .then(({ ticket_fields: fields }) => {
        setAllFields(fields)
        const mapping = loadFieldMapping()
        const restored: LinkedField[] = []
        for (const [zendeskFieldIdStr, vtexKey] of Object.entries(mapping)) {
          const zf = fields.find((f) => f.id === Number(zendeskFieldIdStr))
          if (zf) restored.push({ zendeskField: zf, vtexKey })
        }
        setLinkedFields(restored)
      })
      .catch(() => setAllFields([]))
      .finally(() => setIsLoading(false))
  }, [])

  const availableFields = allFields.filter(
    (f) => !linkedFields.find((lf) => lf.zendeskField.id === f.id)
  )

  function addField(field: ZendeskTicketField) {
    setLinkedFields((prev) => [...prev, { zendeskField: field, vtexKey: '' }])
  }

  function removeField(zendeskFieldId: number) {
    setLinkedFields((prev) => prev.filter((lf) => lf.zendeskField.id !== zendeskFieldId))
  }

  function setVtexKey(zendeskFieldId: number, vtexKey: string) {
    setLinkedFields((prev) =>
      prev.map((lf) => (lf.zendeskField.id === zendeskFieldId ? { ...lf, vtexKey } : lf))
    )
  }

  function getAvailableVtexOptions() {
    return VTEX_PROPERTIES.map((p) => ({
      value: p.key,
      label: p.path,
    }))
  }

  function handleSave() {
    const mapping: FieldMapping = {}
    const labels: FieldMappingLabels = {}
    for (const lf of linkedFields) {
      if (!lf.vtexKey) continue
      const zendeskFieldId = String(lf.zendeskField.id)
      mapping[zendeskFieldId] = lf.vtexKey
      const vtexProp = VTEX_PROPERTIES.find(p => p.key === lf.vtexKey)
      labels[zendeskFieldId] = {
        vtexKey: lf.vtexKey,
        vtexLabel: vtexProp?.label ?? lf.vtexKey,
        zendeskTitle: lf.zendeskField.title,
      }
    }
    saveFieldMapping(mapping, labels)
    zafClient?.invoke('destroy')
  }

  return {
    availableFields,
    linkedFields,
    isLoading,
    addField,
    removeField,
    setVtexKey,
    getAvailableVtexOptions,
    handleSave,
    showInfo,
    setShowInfo,
  }
}

